import { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Typography from "@mui/material/Typography";
import { Link as RouterLink } from "react-router";
import { useTranslation } from "@privacy/translations";
import { useQuery } from "@tanstack/react-query";
import {
  customerLibraryQueryOptions,
  myTenantsHierarchyWithCreateRegistrationPermissionQueryOptions,
  responsibleCandidatesForTenantsQueryOptions,
  type CustomerLibraryProcessing,
} from "../api/registrations";
import { queryClient } from "../api/queryClient";
import { prefetch } from "../api/prefetch";
import { EmptyState } from "../shared/EmptyState";
import { PlusIcon, SearchIcon } from "../shared/icons";
import { PageSpinner, PageTitle, PageWrapper } from "../shared/Page";
import { usePrivacyNavigate } from "../shared/usePrivacyNavigate";
import { usePrivacyDocumentTitle } from "../shared/usePrivacyDocumentTitle";
import { useCurrentCustomer } from "../root/CurrentOrganisationProvider";
import { RegistrationVariantProvider } from "../pages/RegistrationsGridPage/RegistrationVariantProvider";
import { AddFromLibraryDialog } from "./AddFromLibraryDialog";
import { DynamicGrid } from "../dynamicGrid";

const prefetchAddFromLibraryDialog = () =>
  void queryClient
    .fetchQuery(myTenantsHierarchyWithCreateRegistrationPermissionQueryOptions())
    .then(tenantHierarchy =>
      queryClient.prefetchQuery(responsibleCandidatesForTenantsQueryOptions([tenantHierarchy.id]))
    );

const CustomerLibraryPage = () => {
  const { t } = useTranslation();
  const currentCustomer = useCurrentCustomer();
  const customerLibraryQuery = useQuery(customerLibraryQueryOptions());
  usePrivacyDocumentTitle("SharedProcessing.OrganisationLibrary.DocumentTitle");

  if (!customerLibraryQuery.data) return <PageSpinner />;

  return (
    <PageWrapper>
      <PageTitle
        title={t("SharedProcessing.Library.Title", { customer: currentCustomer.name })}
        subTitle={t("SharedProcessing.OrganisationLibrary.SubTitle", { customer: currentCustomer.name })}
        wrap
      />
      {customerLibraryQuery.data.length === 0 ? (
        <Box sx={{ height: "50vh" }}>
          <EmptyState labelKey="SharedProcessing.OrganisationLibrary.Empty" />
        </Box>
      ) : (
        <LibraryGroups processings={customerLibraryQuery.data} />
      )}
    </PageWrapper>
  );
};

type LibraryTab = "all" | "notAdded" | "inRegister";

const libraryTabOf = (processing: CustomerLibraryProcessing): Exclude<LibraryTab, "all"> =>
  processing.isAddedToOrganisation ? "inRegister" : "notAdded";

const LibraryGroups = ({ processings }: { processings: Array<CustomerLibraryProcessing> }) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<LibraryTab>("all");
  const [query, setQuery] = useState("");
  const needle = query.trim().toLocaleLowerCase();
  const matching = processings.filter(
    processing => !needle || `${processing.name} ${processing.purpose ?? ""}`.toLocaleLowerCase().includes(needle)
  );
  const inTab = (key: LibraryTab) => matching.filter(processing => key === "all" || libraryTabOf(processing) === key);
  const activeTab = tab === "all" || inTab(tab).length > 0 ? tab : "all";
  const shown = inTab(activeTab);
  const tabs: Array<LibraryTab> = ["all", "notAdded", "inRegister"];

  return (
    <Stack sx={{ gap: 3, mt: 4, maxWidth: 960 }}>
      <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
        <Stack direction="row" sx={{ gap: 2 }}>
          {tabs
            .filter(key => key === "all" || inTab(key).length > 0)
            .map(key => (
              <DynamicGrid.ToolbarTab
                key={key}
                selected={activeTab === key}
                onClick={() => setTab(key)}
                label={t(`SharedProcessing.OrganisationLibrary.Tab.${key}`)}
                count={inTab(key).length}
                sizeToText
              />
            ))}
        </Stack>
        <TextField
          id="library-search"
          size="small"
          type="search"
          value={query}
          onChange={event => setQuery(event.target.value)}
          placeholder={t("SharedProcessing.OrganisationLibrary.SearchPlaceholder")}
          slotProps={{
            input: {
              sx: { borderRadius: 1 },
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon size={14} />
                </InputAdornment>
              ),
            },
            htmlInput: { "aria-label": t("SharedProcessing.OrganisationLibrary.SearchPlaceholder") },
          }}
          sx={{ width: "min(320px, 100%)" }}
        />
      </Stack>
      {shown.length === 0 ? (
        <Box sx={{ height: "40vh" }}>
          <EmptyState
            label={t("SharedProcessing.OrganisationLibrary.NoMatches", { query: query.trim() })}
            action={
              <Button variant="text" onClick={() => setQuery("")}>
                {t("Grid.ClearSearch")}
              </Button>
            }
          />
        </Box>
      ) : (
        <Box>
          {shown.map(processing => (
            <LibraryRow key={processing.id} processing={processing} />
          ))}
        </Box>
      )}
    </Stack>
  );
};

const LibraryRow = ({ processing }: { processing: CustomerLibraryProcessing }) => {
  const { t } = useTranslation();
  const { paths } = usePrivacyNavigate();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  return (
    <Stack
      direction="row"
      sx={theme => ({
        alignItems: "center",
        gap: 3,
        minHeight: 56,
        px: 2.5,
        py: 1,
        borderTop: `1px solid ${theme.palette.divider}`,
      })}
    >
      <Stack sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 600, fontSize: theme => theme.typography.pxToRem(14), overflowWrap: "anywhere" }}>
          {processing.name}
        </Typography>
        {processing.purpose && (
          <Typography
            sx={{
              fontSize: theme => theme.typography.pxToRem(12),
              color: "text.secondary",
              overflowWrap: "anywhere",
              textWrap: "pretty",
            }}
          >
            {processing.purpose}
          </Typography>
        )}
      </Stack>
      {processing.isAddedToOrganisation ? (
        processing.canOpen && (
          <Link
            component={RouterLink}
            to={paths.registrationsInline({ id: processing.id, variant: "Controller" })}
            underline="hover"
            sx={{ flex: "none", fontWeight: 600, fontSize: theme => theme.typography.pxToRem(12) }}
          >
            {t("SharedProcessing.OrganisationLibrary.OpenInRegister")}
          </Link>
        )
      ) : (
        <Button
          variant="outlined"
          size="small"
          startIcon={<PlusIcon size={14} />}
          disabled={!processing.isDistributable}
          onMouseEnter={prefetchAddFromLibraryDialog}
          onClick={() => setIsAddDialogOpen(true)}
          sx={{ flex: "none" }}
        >
          {t("SharedProcessing.PresentIn.Add")}
        </Button>
      )}
      {isAddDialogOpen && <AddFromLibraryDialog processing={processing} onClose={() => setIsAddDialogOpen(false)} />}
    </Stack>
  );
};

export const customerLibraryPageRoute = {
  path: "customer-library",
  element: (
    <RegistrationVariantProvider variant="Controller">
      <CustomerLibraryPage />
    </RegistrationVariantProvider>
  ),
};

export const prefetchCustomerLibraryPage = () => prefetch(customerLibraryQueryOptions());
